import {
    AuthContext,
    BaseButton,
    BaseNavLink,
    createRequestFilters,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderClearinghouseErasRequest,
    ProviderClearinghouseEra,
    ProviderClearinghouseEraSortType,
} from '@enter/core';
import { mdiMenuRight } from '@mdi/js';
import { Icon } from '@mdi/react';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseItemListingRequest, parseSortBy } from 'stores';
import { ClearinghouseStore, EndpointUnavailableError, EraScope } from 'stores/clearinghouse.store';
import { ClearinghouseScopeNotice, ClearinghouseUnavailableNotice } from './clearinghouse-endpoint-notice';
import * as S from './clearinghouses.styles';

interface IClearinghouseErasProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    accountId?: string;
    clearinghouseId?: string;
}

interface IClearinghouseErasState {
    loading: boolean;
    clearinghouseName?: string;
    /** How far the rows are known to belong to this clearinghouse — see EraScope. */
    scope?: EraScope;
    /** Set when the list endpoint answered with an error; the page names it instead of showing "no ERAs". */
    unavailable?: EndpointUnavailableError;
}

const FETCH_GROUP = 'clearinghouse-eras';

/** The same shape the support console lists, minus the fields the provider payload omits. */
const columns: ITableColumn<ProviderClearinghouseEra>[] = [
    {
        id: 'id',
        Header: 'Id',
        accessor: 'id',
        Cell: DataTableCell('String'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'eraId',
        Header: 'ERA Id',
        accessor: 'eraId',
        sortType: ProviderClearinghouseEraSortType.ERAID,
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
    },
    {
        id: 'payerName',
        Header: 'Payer name',
        accessor: 'payerName',
        sortType: ProviderClearinghouseEraSortType.PAYERNAME,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'npi',
        Header: 'Billing NPI',
        accessor: 'npi',
        disableSortBy: true,
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
    },
    {
        id: 'ein',
        Header: 'EIN',
        accessor: 'ein',
        disableSortBy: true,
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
    },
    {
        id: 'providerName',
        Header: 'Provider name',
        accessor: 'providerName',
        sortType: ProviderClearinghouseEraSortType.PROVIDERNAME,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'paymentNumber',
        Header: 'Payment number',
        accessor: 'paymentNumber',
        // An EFT trace number (TRN02) runs to 15+ characters and must not wrap or overrun.
        minWidth: 170,
        width: 170,
        disableSortBy: true,
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
    },
    {
        id: 'paidAmount',
        Header: 'Paid amount',
        accessor: 'paidAmount',
        sortType: ProviderClearinghouseEraSortType.PAIDAMOUNT,
        Cell: DataTableCell('Amount'),
    },
    {
        id: 'paidDate',
        Header: 'Paid date',
        accessor: 'paidDate',
        sortType: ProviderClearinghouseEraSortType.PAIDDATE,
        Cell: DataTableCell('Date'),
    },
    {
        id: 'paymentType',
        Header: 'Payment type',
        accessor: 'paymentType',
        sortType: ProviderClearinghouseEraSortType.PAYMENTTYPE,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'created',
        Header: 'Created',
        accessor: 'created',
        sortType: ProviderClearinghouseEraSortType.CREATED,
        Cell: DataTableCell('DateTime'),
        show: false,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderClearinghouseErasRequest>[] = [
    {
        label: 'Payer name',
        filterId: 'filterPayerName',
        columnId: 'payerName',
        filterType: 'String',
    },
    {
        label: 'Provider name',
        filterId: 'filterProviderName',
        columnId: 'providerName',
        filterType: 'String',
    },
    {
        label: 'NPI',
        filterId: 'filterNpi',
        columnId: 'npi',
        filterType: 'String',
    },
    {
        label: 'ERA Id',
        filterId: 'filterEraId',
        columnId: 'eraId',
        filterType: 'String',
    },
];

/** The table's empty state while the endpoint is unavailable — "not loaded", never "none" (NS-3). */
const NotLoaded: React.FC = () => <S.Cell>Not loaded — see the notice above.</S.Cell>;

class ClearinghouseErasPage extends React.Component<IClearinghouseErasProps, IClearinghouseErasState> {
    private presenter: DataTablePresenter<ProviderClearinghouseEra>;

    constructor(props: IClearinghouseErasProps) {
        super(props);
        this.state = { loading: false };
        this.presenter = new DataTablePresenter<ProviderClearinghouseEra>({
            name: FETCH_GROUP,
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Clearinghouse eras';
        this.loadClearinghouseName();
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup(FETCH_GROUP);
    }

    public render(): React.ReactElement {
        const { accountId } = this.props;
        const { clearinghouseName } = this.state;

        return (
            <PageLayout>
                <PageLayoutRow>
                    <S.Breadcrumbs>
                        <BaseNavLink to={accountId ? `/${accountId}/clearinghouses` : '/clearinghouses'}>
                            {(): React.ReactElement => <BaseButton variant="text">Clearinghouses</BaseButton>}
                        </BaseNavLink>
                        <Icon path={mdiMenuRight} size={0.75} />
                        {!!clearinghouseName && <BaseButton variant="text">{clearinghouseName}</BaseButton>}
                    </S.Breadcrumbs>
                </PageLayoutRow>
                <PageHeader title="Clearinghouse eras" hideDateRange={true} />
                {this.state.unavailable ? (
                    <PageLayoutRow>
                        <ClearinghouseUnavailableNotice
                            error={this.state.unavailable}
                            what="This clearinghouse's ERAs"
                        />
                    </PageLayoutRow>
                ) : (
                    this.state.scope &&
                    this.state.scope !== 'confirmed' && (
                        <PageLayoutRow>
                            <ClearinghouseScopeNotice scope={this.state.scope} />
                        </PageLayoutRow>
                    )
                )}
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            getRowHref={this.getRowHref}
                            NotFoundComponent={this.state.unavailable ? NotLoaded : undefined}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    /** Drills into this ERA's claims — the third level, `:clearinghouseId/eras/:eraId`. */
    private getRowHref = (row: Row<ProviderClearinghouseEra>): string => {
        const { accountId, clearinghouseId } = this.props;
        const path = `clearinghouses/${clearinghouseId}/eras/${row.original.id}`;
        return accountId ? `/${accountId}/${path}` : `/${path}`;
    };

    /** Demo mode must never render real account data (NS-14). */
    private get isDemo(): boolean {
        return !!this.props.rootStore?.isDemo();
    }

    /** Decoration for the breadcrumb — a failure here must not affect the list below. */
    private loadClearinghouseName = async (): Promise<void> => {
        const { clearinghouseId } = this.props;
        if (!clearinghouseId || this.isDemo) {
            return;
        }
        try {
            const clearinghouse = await ClearinghouseStore.get(clearinghouseId);
            this.setState({ clearinghouseName: clearinghouse.name });
        } catch {
            // The id in the URL is the fact; the name is only a label.
        }
    };

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (this.isDemo) {
            this.presenter.onDataUpdate([]);
            this.presenter.onDataTotalChange(0);
            return;
        }

        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup(FETCH_GROUP);
            // The notice above reports a failed read; the global error dialog would repeat it on every page.
            this.props.authContext.setGroup(FETCH_GROUP, { skipErrorHandler: true });

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const { limit, offset, sort, order } = parseItemListingRequest({
                ...parseSortBy(sortBy, columns),
                page: pageIndex,
                pageSize,
            });

            // Asked for this clearinghouse's ERAs; `scope` says whether the rows can be confirmed as such.
            const { listing, scope } = await ClearinghouseStore.listEras(this.props.clearinghouseId || '', {
                ...createRequestFilters(validFilters, true),
                limit,
                offset,
                sort: sort as ProviderClearinghouseEraSortType[] | undefined,
                order: order as ListProviderClearinghouseErasRequest['order'],
            });

            this.presenter.onDataUpdate(listing.items);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({ loading: false, scope, unavailable: undefined });
        } catch (err) {
            if (err instanceof EndpointUnavailableError) {
                this.presenter.onDataUpdate([]);
                this.presenter.onDataTotalChange(0);
                this.setState({ loading: false, scope: undefined, unavailable: err });
                return;
            }
            this.setState({ loading: false });
            this.props.dialogContext.showError(err as Error);
        } finally {
            // `setGroup` moves a global cursor; put the error handler back for whatever fetches next.
            this.props.authContext.setGroup(FETCH_GROUP);
        }
    };
}

export const ClearinghouseEras: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        const { accountId, clearinghouseId } = useParams<{ accountId: string; clearinghouseId: string }>();
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        if (!rootStore) {
            return <></>;
        }
        return (
            <ClearinghouseErasPage
                accountId={accountId}
                clearinghouseId={clearinghouseId}
                authContext={authContext}
                dialogContext={dialogContext}
                rootStore={rootStore}
            />
        );
    }),
);
